import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useEffect, useState } from "react";
import DeliveryCashService from "../../services/deliverycash/deliverycashservice";

interface User {
  userID: number;
  name: string;
  primaryContactNbr: string;
  displayText?: string; 
}

interface StoreCashHandoverDialogProps {
  visible: boolean;
  onHide: () => void;
  data: any;
  setLoading: (loading: boolean) => void;
  toastRef: React.RefObject<Toast>; 
}
const StoreCashHandoverDialog: React.FC<StoreCashHandoverDialogProps> = ({
  visible,
  onHide,
  data,
  setLoading,
  toastRef
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [remarks, setRemarks] = useState<string>("");
  const [referenceNumber, setReferenceNumber] = useState<string>("");

  useEffect(() => {
    if (visible) {
      fetchUsers();
       setAmount(data.unsettledAmount || 0); 
    } else {
      setSelectedUser(null);
       setRemarks("");
       setReferenceNumber("");
    }
  }, [visible]);

  const fetchUsers = async () => {
    try {
      const userList: User[] = await DeliveryCashService.GetusersListCashhandover();

      const mappedUsers = userList.map((user) => ({
        ...user,
        displayText: `${user.name} - ${user.primaryContactNbr}`,
      }));

      setUsers(mappedUsers);
    } catch (error) {
      console.error("Error fetching user list:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load user list",
      });
    }
  };

  const handleSubmit = async () => {
    if (!selectedUser) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select a user for handover",
      });
      return;
    }

     if (!amount || amount <= 0) {
    toastRef.current?.show({
      severity: "warn",
      summary: "Validation",
      detail: "Please enter a valid amount",
    });
    return;
  }

  if (amount > data.unsettledAmount) {
    toastRef.current?.show({
      severity: "warn",
      summary: "Validation",
      detail: "Amount cannot be greater than the unsettled amount",
    });
    return;
  }
    setLoading(true);

    try {
        const requestData = {
          SettleDate: data.invoiceDate,
          RequestSender: data.userID,
          RequestTo: selectedUser.userID,
          Amount: amount,
          ReferenceNumber: referenceNumber,
          Remarks: remarks ,         
    };

    const response = await DeliveryCashService.AddCashHandoverrequest(requestData);
    if (response?.isSuccess) {
      toastRef.current?.show({
        severity: "success",
        summary: "Success",
        detail: response.message || "Cash handover submitted successfully",
      });
      onHide();
    } else {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: response?.message || "Failed to submit handover",
      });
    }
  } catch (error) {
    toastRef.current?.show({
      severity: "error",
      summary: "Error",
      detail: "Unexpected error occurred during handover",
    });
  } finally {
      setLoading(false);
    }
  };

  const selectedUserTemplate = (option: User) => {
    if (option) {
      return (
        <div className="flex flex-col">
         {option.name} - {option.primaryContactNbr}
        </div>
      );
    }
    return <span>Select User</span>;
  };

  const userOptionTemplate = (option: User) => {
    return (
      <div className="flex flex-col">
       {option.name} - {option.primaryContactNbr}
      </div>
    );
  };

  return (
    <>
      {/* <Toast ref={toast} /> */}
      <Dialog
        header="Cash Handover"
        visible={visible}
        onHide={onHide}
        style={{ width: "30vw" }}
        modal
        breakpoints={{ "960px": "90vw" }}
      >
        <div className="p-fluid">
          <div className="mb-3">
            <label className="text-sm font-semibold">Date</label>
            <div className="p-inputtext bg-gray-100 py-2 px-3 rounded">
              {new Date(data.invoiceDate).toLocaleDateString("en-GB")}
            </div>
          </div>

        <div className="mb-3">
        <label className="text-sm font-semibold">Amount</label>
        <input
            type="number"
            className="p-inputtext p-component w-full"
            value={amount}
            onChange={(e) => setAmount(parseFloat(e.target.value))}
            step="0.01"
            placeholder="Enter amount"
            style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
        />
        </div>

          <div className="mb-3">
            <label className="text-sm font-semibold">Select User</label>
            <Dropdown
              value={selectedUser}
              onChange={(e: DropdownChangeEvent) => setSelectedUser(e.value)}
              options={users}
              optionLabel="displayText"
              placeholder="Select User"
              filter
              filterBy="displayText"
              filterMatchMode="contains"
              valueTemplate={selectedUserTemplate}
              itemTemplate={userOptionTemplate}
              className="w-full"
              style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
            />
          </div>

          <div className="mb-3">
            <label className="text-sm font-semibold">Reference Number</label>
            <input
              type="text"
              className="p-inputtext p-component w-full"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              placeholder="Enter reference number"
              style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
            />
          </div>
     
            <div className="mb-3">
            <label className="text-sm font-semibold">Remarks</label>
            <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                className="p-inputtext p-component w-full"
                placeholder="Enter remarks (optional)"
                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
            />
            </div>

       
          <Button
           type="button"
            label="Submit Handover"
            onClick={handleSubmit}
            className="mt-2"
          />
        </div>
      </Dialog>
    </>
  );
};

export default StoreCashHandoverDialog;
